import { EventEmitter, Injectable } from '@angular/core';
import { ListService } from 'bookraw/shared/services/list.service';
import {
  ClassStat,
  DailyStatAndTopReader,
  GeneralClassStats,
  GeneralStatistics,
  GeneralUsersStatistics,
  ReadGoalStatList,
  ReadTimeGoal,
  ScoreStat,
  SubscriptionStatistics
} from 'bookraw/shared/models/statistic.model';
import { Book } from 'bookraw/shared/models/book.model';
import { Observable } from 'rxjs';
import { ItemResponse } from 'bookraw/shared/models/api/item.response';
import { environment } from 'env/environment';
import { User } from 'bookraw/shared/models/user.model';
import { PaginationData } from 'bookraw/shared/models/pagination-data';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { QueryParams } from 'bookraw/shared/models/query-params';
import { Audio } from 'bookraw/shared/components/reusable-components/audio-list.component';
import { ListResponse } from 'bookraw/shared/models/api/list.response';

@Injectable()
export class StatisticsService extends ListService<Book | User | ClassStat> {

  url: string = '/api/admin/statistics';

  usersBookQueryParams: QueryParams;
  usersReadQueryParams: QueryParams;
  userQueryParams: QueryParams;
  bookQueryParams: QueryParams;
  dailyStatQueryParams: QueryParams;
  bookrScoreQueryParams: QueryParams;
  userBooksQueryParams: QueryParams;
  generalQueryParams: QueryParams;
  goalSettingQueryParams: QueryParams;
  readTimeGoalQueryParams: QueryParams;
  classGeneralQueryParams: QueryParams;
  classListQueryParams: QueryParams;
  paginationUsersBookData: PaginationData<User>;
  paginationUsersReadData: PaginationData<User>;
  paginationUserData: PaginationData<User>;
  paginationBookData: PaginationData<Book>;
  goalSettingsChanged: EventEmitter<any> = new EventEmitter<any>();

  constructor(public http: HttpClient) {
    super(http);
    this.usersBookQueryParams = new QueryParams();
    this.usersReadQueryParams = new QueryParams();
    this.userQueryParams = new QueryParams();
    this.bookQueryParams = new QueryParams();
    this.dailyStatQueryParams = new QueryParams();
    this.userBooksQueryParams = new QueryParams();
    this.bookrScoreQueryParams = new QueryParams();
    this.generalQueryParams = new QueryParams();
    this.goalSettingQueryParams = new QueryParams();
    this.readTimeGoalQueryParams = new QueryParams();
    this.classGeneralQueryParams = new QueryParams();
    this.classListQueryParams = new QueryParams();
    this.paginationUsersBookData = new PaginationData<User>(10);
    this.paginationUsersReadData = new PaginationData<User>(10);
    this.paginationUserData = new PaginationData<User>(10);
    this.paginationBookData = new PaginationData<Book>(25);
  }

  get(entity?: string): Observable<PaginationData<Book | User>> {

    if (entity === 'usersBooks') {
      this.usersBookQueryParams.setQueryParam('limit', '' + this.paginationUsersBookData.limit);
      this.usersBookQueryParams.setQueryParam('offset', '' + (this.paginationUsersBookData.offset * this.paginationUsersBookData.limit));
      return this.http.get<PaginationData<User>>(environment.baseURL + this.url + '/users' + this.usersBookQueryParams.paramString).pipe(tap(
        (data) => {
          this.paginationUsersBookData.savePaginationData(data);
        }
      ));
    } else if (entity === 'usersRead') {
      this.usersReadQueryParams.setQueryParam('limit', '' + this.paginationUsersReadData.limit);
      this.usersReadQueryParams.setQueryParam('offset', '' + (this.paginationUsersReadData.offset * this.paginationUsersReadData.limit));
      return this.http.get<PaginationData<User>>(environment.baseURL + this.url + '/users' + this.usersReadQueryParams.paramString).pipe(tap(
        (data) => {
          this.paginationUsersReadData.savePaginationData(data);
        }
      ));
    } else if (entity === 'users') {
      this.userQueryParams.setQueryParam('limit', '' + this.paginationUserData.limit);
      this.userQueryParams.setQueryParam('offset', '' + (this.paginationUserData.offset * this.paginationUserData.limit));
      return this.http.get<PaginationData<User>>(environment.baseURL + this.url + '/' + entity + this.userQueryParams.paramString).pipe(tap(
        (data) => {
          this.paginationUserData.savePaginationData(data);
        }
      ));
    } else if (entity === 'books') {
      this.bookQueryParams.setQueryParam('limit', '' + this.paginationBookData.limit);
      this.bookQueryParams.setQueryParam('offset', '' + (this.paginationBookData.offset * this.paginationBookData.limit));
      return this.http.get<PaginationData<Book>>(environment.baseURL + this.url + '/' + entity + this.bookQueryParams.paramString).pipe(tap(
        (data) => {
          this.paginationBookData.savePaginationData(data);
        }
      ));
    } else if (entity === 'classes') {
      this.bookQueryParams.setQueryParam('limit', '-1');
      this.bookQueryParams.setQueryParam('offset', '');
      return this.http.get<PaginationData<Book>>(environment.baseURL + this.url + '/' + entity + this.bookQueryParams.paramString).pipe(tap(
        (data) => {
          this.paginationBookData.savePaginationData(data);
        }
      ));
    }
  }

  getList(_params?: { [p: string]: string }, entity?: 'classes'): Observable<ListResponse<ClassStat>> {
    if (entity === 'classes') {
      this.queryParams.setQueryParam('limit', null);
      this.queryParams.setQueryParam('offset', null);
      return this.http.get<ListResponse<ClassStat>>(`${environment.baseURL}${this.url}/${entity}${this.classListQueryParams.paramString}`);
    }
  }

  getUserStatistics(userId: number): Observable<ItemResponse<User>> {
    return this.http.get<ItemResponse<User>>(environment.baseURL + this.url + '/users/' + userId + this.userBooksQueryParams.paramString);
  }

  getGeneralStatistic(): Observable<ItemResponse<GeneralStatistics>> {
    return this.http.get<ItemResponse<GeneralStatistics>>(environment.baseURL + this.url + '/general');
  }

  getGeneralUsersStatistic(): Observable<ItemResponse<GeneralUsersStatistics>> {
    return this.http.get<ItemResponse<GeneralUsersStatistics>>(environment.baseURL + this.url + '/users/general' + this.generalQueryParams.paramString);
  }

  getSubscriptionStatistics(): Observable<ItemResponse<SubscriptionStatistics>> {
    return this.http.get<ItemResponse<SubscriptionStatistics>>(environment.baseURL + '/api/admin/subscriptions/statistics');
  }

  getDailyStatAndTopReader(): Observable<ItemResponse<DailyStatAndTopReader>> {
    return this.http.get<ItemResponse<DailyStatAndTopReader>>(environment.baseURL + this.url + '/users/bookRead' + this.dailyStatQueryParams.paramString);
  }

  getScoreStat(id: number): Observable<ItemResponse<ScoreStat>> {
    return this.http.get<ItemResponse<ScoreStat>>(environment.baseURL + this.url + '/users/' + id + '/bookScore' + this.dailyStatQueryParams.paramString);
  }

  getReadGoalStat(): Observable<ItemResponse<ReadGoalStatList>> {
    return this.http.get<ItemResponse<ReadGoalStatList>>(environment.baseURL + this.url + '/users/readGoal' + this.dailyStatQueryParams.paramString);
  }

  getReadTimeGoal(): Observable<ItemResponse<ReadTimeGoal>> {
    return this.http.get<ItemResponse<ReadTimeGoal>>(environment.baseURL + this.url + '/users/readTimeGoal' + this.readTimeGoalQueryParams.paramString);
  }

  getGeneralClassStats(): Observable<ItemResponse<GeneralClassStats>> {
    return this.http.get<ItemResponse<GeneralClassStats>>(
      `${environment.baseURL}${this.url}/classes/general${this.classGeneralQueryParams.paramString}`
    );
  }

  updateReadGoalStat(): Observable<ItemResponse<number>> {
    return this.http.put<ItemResponse<number>>(
      environment.baseURL +
        this.url +
        '/users/readGoal' +
        this.readTimeGoalQueryParams.paramString,
      null
    );
  }

  setAudioScore(statId: number, data: { score: number }): Observable<ItemResponse<Audio>> {
    return this.http.post<ItemResponse<Audio>>(environment.baseURL + this.url + '/users/' + statId + '/audioScore', data);
  }

  getAudioScore(statId: number): Observable<ItemResponse<Audio>> {
    return this.http.get<ItemResponse<Audio>>(environment.baseURL + this.url + '/users/' + statId + '/audioScore');
  }

  generateAudioScore(statId: number): Observable<ItemResponse<Audio>> {
    return this.http.post<ItemResponse<Audio>>(environment.baseURL + this.url + '/users/' + statId + '/audioScoreGenerate', []);
  }

  statusAudioScore(statId: string|number) {
    return this.http.get(environment.baseURL + this.url + '/users/' + statId + '/audioScoreStatus')
  }

  setQueryParam = (key: string, value: string | null | undefined, entity?: string): void => {
    if (entity === 'usersBooks') this.usersBookQueryParams.setQueryParam(key, value);
    if (entity === 'usersRead') this.usersReadQueryParams.setQueryParam(key, value);
    if (entity === 'users') this.userQueryParams.setQueryParam(key, value);
    if (entity === 'books') this.bookQueryParams.setQueryParam(key, value);
    if (entity === 'dailyStat') this.dailyStatQueryParams.setQueryParam(key, value);
    if (entity === 'userBooks') this.userBooksQueryParams.setQueryParam(key, value);
    if (entity === 'bookrScore') this.dailyStatQueryParams.setQueryParam(key, value);
    if (entity === 'goalSetting') this.goalSettingQueryParams.setQueryParam(key, value);
    if (entity === 'readTimeGoal') this.readTimeGoalQueryParams.setQueryParam(key, value);
    if (entity === 'classGeneral') this.classGeneralQueryParams.setQueryParam(key, value);
    if (entity === 'classes') this.classListQueryParams.setQueryParam(key, value);
    if (!entity) this.generalQueryParams.setQueryParam(key, value);
  }

  resetQueryParams(entity?: string) {
    const all = !entity;
    if (all || entity === 'usersBooks') this.usersBookQueryParams.resetQueryParams();
    if (all || entity === 'usersRead') this.usersReadQueryParams.resetQueryParams();
    if (all || entity === 'users') this.userQueryParams.resetQueryParams();
    if (all || entity === 'books') this.bookQueryParams.resetQueryParams();
    if (all || entity === 'dailyStat') this.dailyStatQueryParams.resetQueryParams();
    if (all || entity === 'userBooks') this.userBooksQueryParams.resetQueryParams();
    if (all || entity === 'goalSetting') this.goalSettingQueryParams.resetQueryParams();
    if (all || entity === 'readTimeGoal') this.readTimeGoalQueryParams.resetQueryParams();
    if (all || entity === 'classGeneral') this.classGeneralQueryParams.resetQueryParams();
    if (all || entity === 'classes') this.classListQueryParams.resetQueryParams();
    if (all) this.generalQueryParams.resetQueryParams();
  }

  exportStudentStatistics(schoolId: string, bookIds: string, type: string, userId: string) {
    const headers = new HttpHeaders().set('Accept', 'application/vnd.ms-excel');
    return this.http.get(environment.baseURL + this.url + '/users/' + userId + '/export', {
      headers, observe: 'response', params: { schoolId, bookIds, type}, responseType: 'blob'
    });
  }

  exportRankingStatistics(schoolId: string, type: 'booksCompleted'|'readTime') {
    const tmpQuery = type === 'booksCompleted' ? this.usersBookQueryParams : this.usersReadQueryParams;
    tmpQuery.setQueryParam('limit', null);
    tmpQuery.setQueryParam('offset', null);

    const headers = new HttpHeaders().set('Accept', 'application/vnd.ms-excel');
    return this.http.get(environment.baseURL + this.url + '/users/export' + tmpQuery.paramString, {
      headers, observe: 'response', params: { schoolId, type }, responseType: 'blob'
    });
  }

  exportStatisticsList(schoolId: string, userIds: string) {
    const tmpQuery = this.userQueryParams;
    tmpQuery.setQueryParam('limit', null);
    tmpQuery.setQueryParam('offset', null);

    const headers = new HttpHeaders().set('Accept', 'application/vnd.ms-excel');
    return this.http.get(environment.baseURL + this.url + '/users/exportList' + tmpQuery.paramString, {
      headers, observe: 'response', params: { schoolId, userIds }, responseType: 'blob'
    });
  }
}
